import React from "react";
import { useMediaQuery } from "react-responsive";
import {
  buildLeftcolMiddleRightcolRow,
  buildLeftRightColumnRow,
  buildLeftRightRow,
  buildLeftMiddleRightRow,
} from "../atoms/layoutAtoms";
import { useCapContext } from "../../providers/CapContext";
import { Asset } from "../../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { formatNumber, formatToOneDecimal } from "../../../utils/formatHelpers";
import { RowProps, Term } from "../../capTypes";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";

const productTuple = (
  item: string,
  originalAsset?: Asset,
  activeProposalAsset?: Asset
) => [
  item,
  `${originalAsset?.count ? formatNumber(originalAsset?.count ?? 0, 0) : "-"}`,
  `${originalAsset?.model ?? ""}`,
  `${
    activeProposalAsset?.count
      ? formatNumber(activeProposalAsset?.count ?? 0, 0)
      : "-"
  }`,
  `${activeProposalAsset?.model ?? ""}`,
];

const ProductRows = ({ viewType, isLoading = true }: RowProps) => {
  const {
    prospect,
    originalProposal,
    isOnlyBattery,
    isOnlyEvCharger,
    sow,
    isNonSolar,
  } = useCapContext();
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });

  const activeEnergyCapacity =
    formatToOneDecimal(Number(sow?.usableCapacityKwh)) + " kWh";
  const originalEnergyCapacity =
    formatToOneDecimal(Number(sow?.originalProposal?.usableCapacityKwh)) +
    " kWh";

  const activeEvCharger = formatNumber(
    sow?.proposal?.assetInfo?.filter((asset: Asset) =>
      asset?.type?.toLowerCase().includes("ev charger")
    )[0]?.count ?? 0,
    0
  );
  const originalEvCharger = formatNumber(
    sow?.originalProposal?.assetInfo?.filter((asset: Asset) =>
      asset?.type?.toLowerCase().includes("ev charger")
    )[0]?.count ?? 0,
    0
  );

  const activeMaxChargeRate =
    formatToOneDecimal(Number(sow?.maxChargeRateInA)) + " Amps";
  const originalMaxChargeRate =
    formatToOneDecimal(Number(sow?.originalProposal?.maxChargeRateInA)) +
    " Amps";

  const activeChargingCapability = sow?.powerFlowMode;
  const originalChargingCapability = sow?.originalProposal?.powerFlowMode;

  const activeProposalPanels = prospect?.activeProposal?.assets.find((y: any) =>
    y.type.toLowerCase().includes("panel")
  );
  const originalProposalPanels = originalProposal?.assetInfo.find((y: any) =>
    y.type.toLowerCase().includes("panel")
  );

  const activeMaxPowerOutPut =
    formatToOneDecimal(Number(sow?.systemSizeNameplateAc)) + " kW";
  const originalMaxPowerOutPut =
    formatToOneDecimal(Number(sow?.originalProposal?.systemSizeNameplateAc)) +
    " kW";

  const hasSolarPanels =
    (!!activeProposalPanels || !!originalProposalPanels) && !isNonSolar;

  const activeProposalBatteries = prospect?.activeProposal?.assets.filter(
    (asset: Asset) => {
      return (
        asset?.type?.toLowerCase().includes("battery") ||
        asset?.type?.toLowerCase().includes("energy storage system")
      );
    }
  )[0];

  const originalProposalBatteries = originalProposal?.assetInfo.filter(
    (asset) => {
      return (
        asset?.type?.toLowerCase().includes("battery") ||
        asset?.type?.toLowerCase().includes("energy storage system")
      );
    }
  )[0];

  const hasBattery =
    (!!activeProposalBatteries || !!originalProposalBatteries) &&
    !isOnlyEvCharger;

  // Expansion batteries
  const activeProposalExpansionBatteries: Asset | undefined =
    prospect?.activeProposal?.assets.filter((asset: Asset) => {
      return asset?.model?.toLowerCase().includes("expansion");
    })[0];

  const originalProposalExpansionBatteries = originalProposal?.assetInfo.filter(
    (asset) => {
      return (
        asset?.model?.toLowerCase().includes("expansion") ||
        asset?.sku?.toLowerCase().includes("expansion")
      );
    }
  )[0];

  const hasExpansionBatteries =
    (!!activeProposalExpansionBatteries ||
      !!originalProposalExpansionBatteries) &&
    !isOnlyEvCharger;

  const activeProposalAllInverters: Asset[] =
    prospect?.activeProposal?.assets?.filter((asset: Asset) => {
      return asset?.type?.toLowerCase().includes("inverter");
    });

  // Try to find the word "inverter" by itself
  // (so we can pick "Inverter XYZ" over "Microinverter ABC" option).
  // If that is not available pick the first inverter from the list
  const activeProposalInverters =
    activeProposalAllInverters?.find((asset: Asset) => {
      return asset?.type?.toLowerCase().split(" ").includes("inverter");
    }) ?? activeProposalAllInverters?.[0];

  const originalProposalAllInverters = originalProposal?.assetInfo?.filter(
    (asset: Asset) => {
      return asset?.type?.toLowerCase().includes("inverter");
    }
  );

  const originalProposalInverters =
    originalProposalAllInverters?.find((asset: Asset) => {
      return asset?.type?.toLowerCase().split(" ").includes("inverter");
    }) ?? originalProposalAllInverters?.[0];

  const hasInverter = !!activeProposalInverters || !!originalProposalInverters;

  const activeProposalSpan = prospect?.activeProposal?.assets.filter(
    (asset: any) => {
      return asset.manufacturer.toLowerCase().includes("span");
    }
  )[0];

  const originalProposalSpan = originalProposal?.assetInfo.filter(
    (asset: any) => {
      return asset.manufacturer.toLowerCase().includes("span");
    }
  )[0];

  const hasSmartPanels =
    (!!activeProposalSpan || !!originalProposalSpan) && !isNonSolar;

  const productTypes = [
    hasSolarPanels,
    hasBattery,
    hasExpansionBatteries,
    hasInverter,
    hasSmartPanels,
  ];
  let productCount = 0;

  productTypes.forEach((prodType) => {
    if (prodType) {
      productCount++;
    }
  });

  const rows = [];
  if (hasSolarPanels) {
    let underline = productCount > 1;
    const solarTuple = productTuple(
      "Panels",
      originalProposalPanels,
      activeProposalPanels
    );
    if (viewType === "Single") {
      rows.push(
        buildLeftRightColumnRow(solarTuple, underline, isLoading, isMobile)
      );
    } else {
      rows.push(
        buildLeftcolMiddleRightcolRow(
          solarTuple,
          underline,
          isLoading,
          isMobile
        )
      );
    }
  }
  if (hasBattery) {
    let underline =
      hasInverter || hasSmartPanels || hasExpansionBatteries || isOnlyBattery;
    const batteryTuple = productTuple(
      "Batteries",
      originalProposalBatteries,
      activeProposalBatteries
    );
    if (viewType === "Single") {
      rows.push(
        buildLeftRightColumnRow(batteryTuple, underline, isLoading, isMobile)
      );
    } else {
      rows.push(
        buildLeftcolMiddleRightcolRow(
          batteryTuple,
          underline,
          isLoading,
          isMobile
        )
      );
    }
  }
  if (hasExpansionBatteries) {
    let underline = hasInverter || hasSmartPanels || isOnlyBattery;
    const expansionTuple = productTuple(
      "Expansion batteries",
      originalProposalExpansionBatteries,
      activeProposalExpansionBatteries
    );
    if (viewType === "Single") {
      rows.push(
        buildLeftRightColumnRow(expansionTuple, underline, isLoading, isMobile)
      );
    } else {
      rows.push(
        buildLeftcolMiddleRightcolRow(
          expansionTuple,
          underline,
          isLoading,
          isMobile
        )
      );
    }
  }
  if (isOnlyBattery) {
    const energyCapacityTuple = [
      "Energy Capacity",
      originalEnergyCapacity,
      activeEnergyCapacity,
    ] as Term;
    const maxPowerTuple = [
      "Max Power Output",
      originalMaxPowerOutPut,
      activeMaxPowerOutPut,
    ] as Term;
    if (viewType === "Single") {
      rows.push(
        buildLeftRightRow(energyCapacityTuple, true, isLoading, isMobile),
        buildLeftRightRow(maxPowerTuple, false, isLoading, isMobile)
      );
    } else {
      rows.push(
        buildLeftMiddleRightRow(energyCapacityTuple, true, isLoading, isMobile),
        buildLeftMiddleRightRow(maxPowerTuple, false, isLoading, isMobile)
      );
    }
  }
  if (isOnlyEvCharger) {
    const evChargerTuple = [
      "EV charger",
      originalEvCharger,
      activeEvCharger,
    ] as Term;
    const maxChargeRateTuple = [
      "Max charge rate",
      originalMaxChargeRate,
      activeMaxChargeRate,
    ] as Term;
    const chargingCapabilityTuple = [
      "Charging capability",
      originalChargingCapability,
      activeChargingCapability,
    ] as Term;
    if (viewType === "Single") {
      rows.push(
        buildLeftRightRow(evChargerTuple, true, isLoading, isMobile),
        buildLeftRightRow(maxChargeRateTuple, true, isLoading, isMobile),
        buildLeftRightRow(chargingCapabilityTuple, false, isLoading, isMobile)
      );
    } else {
      rows.push(
        buildLeftMiddleRightRow(evChargerTuple, true, isLoading, isMobile),
        buildLeftMiddleRightRow(maxChargeRateTuple, true, isLoading, isMobile),
        buildLeftMiddleRightRow(
          chargingCapabilityTuple,
          false,
          isLoading,
          isMobile
        )
      );
    }
  }
  if (hasInverter) {
    let underline = hasSmartPanels;
    const inverterTuple = productTuple(
      "Inverters",
      originalProposalInverters,
      activeProposalInverters
    );

    if (viewType === "Single") {
      rows.push(
        buildLeftRightColumnRow(inverterTuple, underline, isLoading, isMobile)
      );
    } else {
      rows.push(
        buildLeftcolMiddleRightcolRow(
          inverterTuple,
          underline,
          isLoading,
          isMobile
        )
      );
    }
  }
  if (hasSmartPanels) {
    const spanTuple = productTuple(
      "Smart Panels",
      originalProposalSpan,
      activeProposalSpan
    );
    if (viewType === "Single") {
      rows.push(buildLeftRightColumnRow(spanTuple, false, isLoading, isMobile));
    } else {
      rows.push(
        buildLeftcolMiddleRightcolRow(spanTuple, false, isLoading, isMobile)
      );
    }
  }

  return <>{rows.map((row) => row)}</>;
};

export { ProductRows, productTuple };
